import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '@app/core/i18n';

import { CharacterRequirement, FieldBehaviorMode } from '@app/queryTyping';

import { PasswordControl } from '@app/common/components/controls/PasswordControl/PasswordControl';
import { Fields } from '@app/common/configurable-wizards/types';
import { getFieldErrorMessage } from '@app/common/configurable-wizards/utils';
import { FormValidationOptions } from '@app/common/types/form';
import { useDebouncedFunction } from '@app/common/utils/hooks/useDebouncedFunction';
import { useCommonValidationMessages } from '@app/common/valiation/commonValidationMessages';

import { CFFormField } from '../common/CFFormField';
import { ShowHidePasswordButton } from '../Password/ShowHidePasswordButton';

import { PasswordValidationRules } from './types';

/**
 * Defines the timeout used to debounce 'onFocusChange' handler calling
 */
export const ON_FOCUS_CHANGE_DEBOUNCE_TIMER = 10;

export interface PasswordInputProps {
  readonly field: Fields['Password'];
  readonly fieldName: string;
  readonly onFocusChange?: (isActive: boolean) => void,
  readonly onPasswordShownToggle: (isPasswordShown: boolean) => void
}

export type PasswordInputHtmlProps = Pick<React.InputHTMLAttributes<HTMLInputElement>, 'aria-describedby'>;

export const PasswordInput: React.FC<PasswordInputProps & PasswordInputHtmlProps> = ({
  field,
  fieldName,
  onFocusChange,
  onPasswordShownToggle,
  ...htmlInputProps
}) => {
  const { t } = useTranslation();
  const {
    register, formState: { errors }, setValue,
  } = useFormContext();
  const [isPasswordShown, setPasswordShown] = useState(false);
  const commonValidationMessages = useCommonValidationMessages();
  const error = getFieldErrorMessage(fieldName, errors, commonValidationMessages);
  const { passwordPolicy } = field;

  // debounce focus-changing handling between input and its icon button (show/hide password)
  // this would add latency of focusout event emiting during focusing between input and its button
  const onFocusChangeDebounced = useDebouncedFunction((isActive: boolean) => {
    if (typeof onFocusChange === 'function') {
      onFocusChange(isActive);
    }
  }, ON_FOCUS_CHANGE_DEBOUNCE_TIMER);

  const onShowHidePasswordClick = () => {
    const isShown = !isPasswordShown;
    setPasswordShown(isShown);
    onPasswordShownToggle(isShown);
  };

  const validationOptions = useMemo<FormValidationOptions>(() => {
    const rules: FormValidationOptions = {
      [PasswordValidationRules.required]: field.fieldBehaviorMode === FieldBehaviorMode.REQUIRED,
    };

    if (!passwordPolicy) { return rules; }

    if (passwordPolicy.minimumCharacterLength) {
      rules[PasswordValidationRules.minLength] = {
        value: passwordPolicy.minimumCharacterLength,
        message: t(
          'password.validation.minlength|Minimum length - validation message',
          'Password must be at least {{minLength}} characters long',
          { minLength: passwordPolicy.minimumCharacterLength },
        ),
      };
    }

    if (passwordPolicy.maximumCharacterLength) {
      rules[PasswordValidationRules.maxLength] = {
        value: passwordPolicy.maximumCharacterLength,
        message: t(
          'password.validation.maxlength|Maximum length - validation message',
          'Password must be up to {{maxLength}} characters long',
          { maxLength: passwordPolicy.maximumCharacterLength },
        ),
      };
    }

    if (passwordPolicy.firstCharacterMustBeAlphabetic) {
      rules.validate = {
        ...rules.validate,
        [PasswordValidationRules.firstCharAlphabetic]: (value) => (
          /^[A-z]/.test(value) || t(
            'password.validation.first-alphabetic|First letter must be alphabetic - validation message',
            'First letter must be alphabetic',
          ) as string
        ),
      };
    }

    if (passwordPolicy.anyCharacterMustBeAlphabetic) {
      rules.validate = {
        ...rules.validate,
        [PasswordValidationRules.anyAlphabetic]: (value) => (
          /[A-z]/g.test(value) || t(
            'password.validation.any-alphabetic|Must contain at least one alphabetic letter - validation message',
            'Must contain at least one alphabetic letter',
          ) as string
        ),
      };
    }

    if (passwordPolicy.lowerCaseRequirement === CharacterRequirement.ONEREQUIRED) {
      rules.validate = {
        ...rules.validate,
        [PasswordValidationRules.anyLowerCase]: (value) => (
          /[a-z]/g.test(value) || t(
            'password.validation.any-lowercase|At least one lowercase letter - validation message',
            'Must contain at least one lowercase letter',
          ) as string
        ),
      };
    } else if (passwordPolicy.lowerCaseRequirement === CharacterRequirement.NOTALLOWED) {
      rules.validate = {
        ...rules.validate,
        [PasswordValidationRules.noLowerCase]: (value) => (
          !/[a-z]/g.test(value) || t(
            'password.validation.no-lowercase|Must not contain lowercase letters - validation message',
            'Must not contain lowercase letters',
          ) as string
        ),
      };
    }

    if (passwordPolicy.upperCaseRequirement === CharacterRequirement.ONEREQUIRED) {
      rules.validate = {
        ...rules.validate,
        [PasswordValidationRules.anyUpperCase]: (value) => (
          /[A-Z]/g.test(value) || t(
            'password.validation.any-uppercase|At least one uppercase letter - validation message',
            'Must contain at least one uppercase letter',
          ) as string
        ),
      };
    } else if (passwordPolicy.upperCaseRequirement === CharacterRequirement.NOTALLOWED) {
      rules.validate = {
        ...rules.validate,
        [PasswordValidationRules.noUpperCase]: (value) => (
          !/[A-Z]/g.test(value) || t(
            'password.validation.no-uppercase|Must not contain uppercase letters - validation message',
            'Must not contain uppercase letters',
          ) as string
        ),
      };
    }

    if (passwordPolicy.numberRequirement === CharacterRequirement.ONEREQUIRED) {
      rules.validate = {
        ...rules.validate,
        [PasswordValidationRules.anyNumber]: (value) => (
          /[\d]/g.test(value) || t(
            'password.validation.any-number|At least one number - validation message',
            'Must contain at least one number',
          ) as string
        ),
      };
    } else if (passwordPolicy.numberRequirement === CharacterRequirement.NOTALLOWED) {
      rules.validate = {
        ...rules.validate,
        [PasswordValidationRules.noNumber]: (value) => (
          !/[\d]/g.test(value) || t(
            'password.validation.no-number|Must not contain numbers - validation message',
            'Must not contain numbers',
          ) as string
        ),
      };
    }

    if (passwordPolicy.specialCharacterRequirement === CharacterRequirement.ONEREQUIRED) {
      rules.validate = {
        ...rules.validate,
        [PasswordValidationRules.anySpecialChar]: (value) => (
          /[^A-Za-z0-9]/g.test(value) || t(
            'password.validation.any-special-char|At least one special char - validation message',
            'Must contain at least one special character',
          ) as string
        ),
      };
    } else if (passwordPolicy.specialCharacterRequirement === CharacterRequirement.NOTALLOWED) {
      rules.validate = {
        ...rules.validate,
        [PasswordValidationRules.noSpecialChar]: (value) => (
          !/[^A-Za-z0-9]/g.test(value) || t(
            'password.validation.no-special-char|Must not contain special char - validation message',
            'Must not contain special characters',
          ) as string
        ),
      };
    }

    return rules;
  }, [field]);

  const { onBlur, ...restRegiesterOptions } = register(fieldName, validationOptions);

  return (
    <CFFormField error={error}>
      <PasswordControl
        {...htmlInputProps}
        label={field.label || ''}
        aria-invalid={!!error}
        autoComplete="new-password"
        type={isPasswordShown ? 'text' : 'password'}
        minLength={passwordPolicy?.minimumCharacterLength || field.minLength}
        maxLength={passwordPolicy?.maximumCharacterLength || field.maxLength}
        trailingChild={(
          <ShowHidePasswordButton
            title={isPasswordShown
              ? t('password.hide-password-btn.title|Hide-password button title message', 'Hide password')
              : t('password.show-password-btn.title|Show-password button title message', 'Show password')}
            aria-label={
              isPasswordShown
                ? t('password.hide-password-btn.aria-label|Hide-password button aria-label message', 'Hide password')
                : t('password.show-password-btn.aria-label|Show-password Button aria-label message', 'Show password')
            }
            onShowHidePassword={onShowHidePasswordClick}
          />
        )}
        data-stable-name={`${field.id}Input`}
        onFocus={() => onFocusChangeDebounced(true)}
        onBlur={(event) => {
          setValue(fieldName, event.target.value?.trim());

          onBlur(event);
          onFocusChangeDebounced(false);
        }}
        {...restRegiesterOptions}
      />
    </CFFormField>
  );
};
